import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SpinningSun from '../common/SpinningSun';

/**
 * ProtectedRoute - Route-level authentication and authorization
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Components to render if authorized
 * @param {string} [props.role] - Required role
 * @param {string} [props.permission] - Required permission
 * @param {string} [props.franchiseId] - Required franchise access
 * @param {string} [props.redirectTo="/login"] - Where to redirect unauthorized users
 * @param {boolean} [props.requireActive=true] - Require active user status
 */
export const ProtectedRoute = ({ 
  children, 
  role, 
  permission, 
  franchiseId,
  redirectTo = "/login",
  requireActive = true
}) => {
  const { 
    user, 
    userProfile, 
    hasPermission, 
    hasAccessToFranchise, 
    loading 
  } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <SpinningSun 
        fullscreen={true}
        message="Checking authentication..."
        size="extra-large"
      />
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Redirect if no user profile (shouldn't happen but safety check)
  if (!userProfile) {
    return <Navigate to="/auth/setup" state={{ from: location }} replace />;
  }

  // Check if user is active (handle both boolean and string values)
  const isUserActive = userProfile.isActive === true || userProfile.isActive === 'true';
  if (requireActive && !isUserActive) {
    return <Navigate to="/auth/inactive" state={{ from: location }} replace />;
  }

  // Admin override
  const isAdmin = userProfile.globalRole === 'administrator';
  if (isAdmin) {
    return children;
  }

  // Role check
  if (role && userProfile.globalRole !== role) {
    return <Navigate to="/auth/unauthorized" state={{ from: location }} replace />;
  }

  // Permission check
  if (permission && !hasPermission(permission, franchiseId)) {
    return <Navigate to="/auth/unauthorized" state={{ from: location }} replace />;
  }

  // Franchise access check
  if (franchiseId && !hasAccessToFranchise(franchiseId)) {
    return <Navigate to="/auth/unauthorized" state={{ from: location }} replace />;
  }

  return children;
};

/**
 * AdminRoute - Route that requires administrator role
 */
export const AdminRoute = ({ children, redirectTo = "/dashboard" }) => {
  return (
    <ProtectedRoute role="administrator" redirectTo={redirectTo}>
      {children}
    </ProtectedRoute>
  );
};

/**
 * FranchiseeRoute - Route that requires franchisee or admin role
 */
export const FranchiseeRoute = ({ children, redirectTo = "/dashboard" }) => {
  const { userProfile, loading } = useAuth();

  if (loading) {
    return (
      <SpinningSun 
        fullscreen={true}
        message="Checking permissions..."
        size="extra-large"
      />
    );
  }

  const hasAccess = userProfile?.globalRole === 'administrator' || 
                   userProfile?.globalRole === 'franchisee';

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

/**
 * PublicRoute - Route that redirects authenticated users
 */
export const PublicRoute = ({ children, redirectTo = "/dashboard" }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <SpinningSun 
        fullscreen={true}
        message="Checking permissions..."
        size="extra-large"
      />
    );
  }

  if (user) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default ProtectedRoute; 